/**
 * `dataLoop` namespace — English. Declared against the French source type, so a
 * missing or extra key fails the build.
 */

import type { DataLoopKeys } from './fr';

const dataLoopEn: DataLoopKeys = {
  common: {
    copy: 'Copy',
    copied: 'Copied ✓',
    refresh: 'Refresh',
    retry: 'Retry',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading…',
    none: '—',
    yes: 'Yes',
    no: 'No',
    from: 'From',
    to: 'To',
    readOnly: 'Read-only',
    readOnlyHint: 'Your role allows viewing only.',
    never: 'Never',
    total: 'Total',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
  },
  errors: {
    plan: 'This feature is not included in your plan (advanced analytics).',
    unavailable: 'Coming soon — this service is not configured on this environment yet.',
    llm: 'The AI assistant is temporarily unavailable. Try again later — no credits were used.',
    credits: 'Not enough AI credits for this action. Top up your credits and try again.',
    forbidden: 'Access denied — your role does not allow this action.',
    notFound: 'Item not found — it may have been deleted.',
    validation: 'Invalid data: {{detail}}',
    validationGeneric: 'Invalid data — check the fields you entered.',
    conflict: 'Conflict: {{detail}}',
    conflictGeneric: 'Conflict: this item already exists.',
    rateLimited: 'Too many requests — wait a moment and try again.',
    network: 'Service unreachable. Check your connection and try again.',
    generic: 'Something went wrong. Please try again.',
  },
  install: {
    title: 'Analytics SDK installation',
    subtitle:
      'Create a publishable key, install the snippet on your site (or let Cursor / Lovable / Claude Code do it) and watch the first events arrive live. Law 25 / CASL consent respected.',
    keysTitle: 'Ingest keys',
    noKeys: 'No keys',
    noKeysHint: 'Create a key to get your installation snippet.',
    createTitle: 'New key',
    name: 'Name',
    namePlaceholder: 'Marketing site',
    origins: 'Allowed origins',
    originsHint: 'One per line, full origin (https://example.com) or wildcard (https://*.example.com). 1 to 20.',
    originsInvalid: 'Invalid origins: {{list}}',
    originsCount: 'Between 1 and 20 origins are required.',
    noConsentMode: 'Without consent',
    modeAggregate: 'Anonymous aggregate (pageview without identifier)',
    modeDrop: 'Nothing (no events)',
    modeHint: 'What the SDK sends until the visitor consents. No identifier is ever created without consent.',
    create: 'Create key',
    creating: 'Creating…',
    created: 'Key created.',
    statusActive: 'Active',
    statusRevoked: 'Revoked',
    events: '{{count}} event(s)',
    lastEvent: 'Last event: {{when}}',
    createdAt: 'Created on {{when}}',
    publishableKey: 'Publishable key',
    selectKey: 'Select a key to see the installation instructions.',
    editTitle: 'Edit key',
    saved: 'Key updated.',
    revoke: 'Revoke',
    revokeTitle: 'Revoke the key?',
    revokeMessage:
      'The key “{{name}}” will immediately stop accepting events. Data already collected is kept. This is permanent for this key.',
    revoked: 'Key revoked.',
    hardDelete: 'Delete permanently',
    hardDeleteTitle: 'Delete the key and its data?',
    hardDeleteMessage: 'The revoked key “{{name}}” and ALL its events will be permanently deleted. This cannot be undone.',
    hardDeleted: 'Key and events deleted.',
    revokedNotice: 'This key is revoked: the SDK can no longer use it. Create a new key to reinstall.',
    tabSnippet: 'Snippet',
    tabPrompt: 'Prompt for Cursor / Lovable / Claude Code',
    tabFrameworks: 'Frameworks',
    tabVerify: 'Verification',
    snippetLead: 'Paste these two tags into the <head> of every page (or of the root template), as high as possible.',
    bannerTitle: 'Consent banner',
    bannerAuto: 'data-banner="auto" shows the built-in FR/EN banner: no identified event is sent before acceptance.',
    bannerOff:
      'data-banner="off" if your site already has its own consent management platform (CMP): then call karexia(\'consent\',\'grant\') or karexia(\'consent\',\'deny\').',
    apiTitle: 'SDK API',
    apiTrack: 'Conversion event (e.g. signup).',
    apiTrackProps: 'Event with properties (e.g. plan payment).',
    apiIdentify: 'Links the visitor to your user ID. The email is SHA-256-hashed in the browser, never sent in clear.',
    apiConsent: 'Passes the consent decision (when data-banner="off").',
    apiVariant: 'Assigns an experiment variant and records the exposure (returns the variant).',
    apiExpose: 'Records exposure to a variant your code already chose.',
    apiReset: 'Forgets the identity (on logout).',
    autoTitle: 'Captured automatically',
    autoConsented: 'After consent: pageviews (including SPA navigation), clicks and form submits (without field values).',
    autoAggregate: 'Without consent (aggregate mode): a single anonymous aggregate pageview, no identifier or cookie.',
    autoDrop: 'Without consent (“nothing” mode): no events.',
    promptLead:
      'Copy this prompt into Cursor, Lovable or Claude Code: it contains your key, the SDK URL, the exact steps and the consent requirement.',
    promptTemplate:
      'Install the Karexia first-party analytics SDK on this website.\n\nPublishable key: {{key}}\nSDK URL: {{sdkUrl}}\nAllowed origins: {{origins}}\n\nSteps:\n1. Add exactly these two tags to the <head> of every page (root HTML template or the app\'s root layout), as high as possible:\n\n{{snippet}}\n\n2. Do not change the key or the URL. The site must be served from one of the allowed origins, otherwise events are rejected.\n3. Consent (Law 25 / CASL): no identified event is sent before consent. With data-banner="auto" the SDK shows its own FR/EN banner. If the site already has a consent management platform, use data-banner="off" instead and call karexia(\'consent\',\'grant\') when the visitor accepts audience measurement, karexia(\'consent\',\'deny\') if they refuse. Never send identified events without consent.\n4. Pageviews (including SPA navigation), clicks and form submits (without field values) are captured automatically after consent: do not add manual tracking for those.\n5. Add the conversion events:\n   - after a successful signup: karexia(\'track\',\'signup\')\n   - after a successful payment: karexia(\'track\',\'paid\',{plan:\'pro\'})\n   - after login: karexia(\'identify\',\'<user id>\',{email:\'<email>\'}) — the email is SHA-256-hashed in the browser, never sent in clear\n   - on logout: karexia(\'reset\')\n6. A/B experiment: karexia(\'variant\',\'<experiment-key>\',[\'control\',\'b\']) returns and records the variant; if your code already picks the variant, call karexia(\'expose\',\'<experiment-key>\',\'b\').\n7. In TypeScript, declare window.karexia as (...args: unknown[]) => unknown.\n8. Verify: open the site, accept consent, visit two pages; the Karexia “Verification” tab must show “Installed ✓”.',
    frameworksLead: 'Ready-to-paste code for your stack.',
    fwReactLead: 'React (Vite / CRA): add the snippet to the <head> of index.html, then call the SDK from your components through this small module.',
    fwNextLead: 'Next.js (App Router): load the SDK in app/layout.tsx with next/script. Client-side navigation is captured automatically.',
    fwVueLead: 'Vue 3 (Vite): add the snippet to the <head> of index.html, then expose the SDK through a plugin. Route changes are captured automatically.',
    fwHtmlLead: 'Static HTML site: the snippet in the <head>, then direct calls.',
    verifyLead: 'Open your site from an allowed origin and accept consent: this screen refreshes every 3 seconds.',
    waiting: 'Waiting for the first event…',
    installed: 'Installed ✓',
    revokedStatus: 'Revoked',
    eventCount: 'Events received',
    consented: 'With consent',
    anonymous: 'Anonymous aggregate',
    rejected: 'Rejected (origin not allowed / key revoked)',
    subjects: 'Consenting visitors',
    lastOrigin: 'Last origin',
    lastEventAt: 'Last event',
    polling: 'Auto-refresh every 3 s',
    pollError: 'Status temporarily unavailable — retrying in 3 s.',
  },
  flows: {
    title: 'Flow map',
    subtitle: 'The real paths taken by your consenting visitors from a start event: volume per step, transitions and drop-off rates.',
    startEvent: 'Start event',
    startEventHint: '$pageview by default (or signup, $click…).',
    maxSteps: 'Max steps',
    topN: 'Paths shown',
    key: 'SDK key',
    allKeys: 'All keys',
    analyze: 'Analyze',
    analyzing: 'Analyzing…',
    sequences: 'Sequences analyzed',
    empty: 'No paths',
    emptyHint:
      'No consented sequence starts with this event in the period. Install the SDK (Analytics → SDK installation) or widen the dates.',
    step: 'Step {{n}}',
    pathsTitle: 'Top paths',
    path: 'Path',
    count: 'Sequences',
    share: 'Share',
    completed: 'Complete',
    exited: 'Exit',
    dropoffsTitle: 'Drop-offs per step',
    entered: 'Entered',
    continued: 'Continued',
    dropped: 'Dropped',
    dropoffRate: 'Drop-off rate',
    linkTitle: '{{source}} → {{target}}: {{count}}',
  },
  subjects: {
    title: 'Recent subjects',
    subtitle: 'Visitors who gave consent (a visitor who refuses never appears here). Click to see their full journey.',
    empty: 'No consenting visitors',
    emptyHint: 'Visitors appear here as soon as they accept consent on a site running the SDK.',
    subject: 'Subject',
    firstSeen: 'First visit',
    lastSeen: 'Last activity',
    events: 'Events',
    identified: 'Identified',
    anonymous: 'Not identified',
    timelineTitle: 'Subject journey',
    userId: 'User: {{id}}',
    noTimeline: 'No events for this subject.',
    kindEvent: 'Event',
    kindTouchpoint: 'Touchpoint',
    kindConversion: 'Conversion',
    pageview: 'Pageview',
    click: 'Click',
    formSubmit: 'Form submitted',
    revenue: 'Revenue: {{amount}}',
    eventsCount: '{{count}} event(s)',
    touchpointsCount: '{{count}} touchpoint(s)',
    conversionsCount: '{{count}} conversion(s)',
    aiSummary: 'AI summary',
    aiSummarizing: 'Summarizing…',
    aiMetered: 'Billed in AI credits.',
    aiSummaryTitle: 'AI journey summary',
  },
  mrr: {
    title: 'MRR & signups by source',
    subtitle:
      'Signups, trials, paying customers, collected revenue and MRR attributed to the acquisition source (Stripe + SDK). Period: the report dates above.',
    source: 'Source',
    signups: 'Signups',
    trials: 'Trials',
    paid: 'Paying',
    paidRevenue: 'Paid revenue',
    mrr: 'MRR',
    totals: 'Total',
    empty: 'No revenue data',
    emptyHint: 'Connect Stripe and install the SDK to attribute signups, trials and MRR to their acquisition sources.',
    connectStripe: 'Connect Stripe',
    unknownSource: '(direct / unknown)',
  },
  stripe: {
    title: 'Stripe',
    subtitle: 'Subscriptions, invoices and MRR for revenue attribution by source.',
    connected: 'Connected',
    notConnected: 'Not connected',
    test: 'TEST',
    live: 'LIVE',
    apiKey: 'Restricted or secret API key',
    apiKeyHint: 'rk_test_… / sk_test_… (test mode) or rk_live_… / sk_live_…. Read-only recommended (restricted key).',
    apiKeyInvalid: 'The key must start with sk_test_, sk_live_, rk_test_ or rk_live_.',
    connect: 'Connect',
    connecting: 'Connecting…',
    connectedToast: 'Stripe connected.',
    connectOauth: 'Stripe Connect (OAuth)',
    connectPending: 'Stripe Connect awaiting approval — use a restricted key (test mode works right now).',
    account: 'Account: {{label}}',
    lastSync: 'Last sync: {{when}}',
    syncNow: 'Sync now',
    syncing: 'Syncing…',
    synced: 'Sync complete.',
    lastResult:
      '{{customers}} customer(s), {{subscriptions}} subscription(s), {{invoices}} invoice(s), {{conversions}} conversion(s) posted. {{delivered}}',
    analyticsDelivered: 'Sent to analytics.',
    analyticsNotDelivered: 'Not yet sent to analytics.',
    syncError: 'Sync error: {{error}}',
    webhookTitle: 'Webhook',
    webhookUrl: 'Webhook URL (add it in Stripe → Developers → Webhooks)',
    webhookSecret: 'Signing secret (whsec_…)',
    webhookSecretInvalid: 'The secret must start with whsec_.',
    webhookConfigured: 'Secret configured',
    webhookMissing: 'Secret not configured',
    webhookSaved: 'Webhook secret saved.',
    purgeOnDisconnect: 'Purge Stripe data on disconnect',
    purgeSaved: 'Preference saved.',
    disconnect: 'Disconnect',
    disconnectTitle: 'Disconnect Stripe?',
    disconnectMessage: 'Choose whether the data already imported (customers, subscriptions, conversions) should be purged or kept.',
    disconnectPurge: 'Disconnect and purge',
    disconnectRetain: 'Disconnect and keep',
    disconnected: 'Stripe disconnected.',
    disconnectedPurged: 'Stripe disconnected — data purged.',
    blocker: 'Blocker: {{blocker}}',
  },
  gsc: {
    title: 'Google Search Console',
    subtitle: 'Organic queries and pages: clicks, impressions, CTR and average position.',
    connected: 'Connected',
    notConnected: 'Not connected',
    notConfigured: 'Configuration required (owner’s Google OAuth application).',
    connect: 'Connect Google Search Console',
    connecting: 'Redirecting…',
    disconnect: 'Disconnect',
    disconnectTitle: 'Disconnect Google Search Console?',
    disconnectMessage: 'OAuth access will be revoked. Data already synced stays available until it is purged.',
    disconnected: 'Search Console disconnected.',
    site: 'Property (site)',
    selectSite: 'Choose a property…',
    siteSaved: 'Property saved.',
    noSites: 'No property accessible with this Google account.',
    lastSync: 'Last sync: {{when}}',
    rowsStored: '{{count}} row(s) stored',
    sync: 'Sync (28 d)',
    syncing: 'Syncing…',
    synced: 'Sync complete.',
    queries: 'Queries',
    pages: 'Pages',
    query: 'Query',
    page: 'Page',
    clicks: 'Clicks',
    impressions: 'Impressions',
    ctr: 'CTR',
    position: 'Position',
    dailyTitle: 'Clicks per day',
    empty: 'No Search Console data',
    emptyHint: 'Choose a property, then run a sync.',
    emptySynced: 'Google has no queries or pages for this property in this period yet. New properties can take 1–2 days to show data.',
    purge: 'Purge data',
    purgeTitle: 'Purge Search Console data?',
    purgeMessage: 'All Search Console rows stored for your account will be permanently deleted.',
    purged: '{{count}} row(s) deleted.',
    blocker: 'Blocker: {{blocker}}',
    load: 'Show',
    properties: 'Tracked properties',
    connectFirst: 'Connect your Google account to list and track Search Console properties.',
    noProperties: 'No property tracked yet.',
    noPropertiesHint: 'Add a Search Console property from your Google account, then sync it.',
    usage: '{{used}} / {{limit}} properties',
    usageUnlimited: '{{used}} properties (unlimited plan)',
    usageUnknown: '{{used}} tracked properties',
    atLimit: 'Your plan’s property limit is reached.',
    upgrade: 'Upgrade your plan',
    planLimitError: 'Plan limit reached ({{used}} / {{limit}} properties). Upgrade your plan to add more.',
    overLimitNotice: 'Some properties are above your plan limit: their data stays viewable but they are no longer synced.',
    readOnly: 'read-only (above plan limit)',
    addProperty: 'Add property',
    adding: 'Adding…',
    pickProperty: 'Choose a property…',
    label: 'Label (optional)',
    labelPlaceholder: 'e.g. Main site',
    noAvailable: 'Every property accessible with this Google account is already tracked.',
    availableBlocker: 'Google properties unavailable: {{blocker}}',
    added: 'Property added.',
    alreadyTracked: 'This property is already tracked.',
    siteNotAvailable: 'This property is not accessible with the connected Google account.',
    invalidSite: 'Invalid property: use sc-domain:example.com or an https:// URL prefix.',
    notConnectedError: 'Connect Google Search Console first.',
    googleUnavailable: 'Google Search Console is unreachable right now. Try again in a moment.',
    syncNow: 'Sync now',
    syncAll: 'Sync all (28 d)',
    syncSelected: 'Sync this property (28 d)',
    syncedRows: 'Sync complete: {{count}} row(s).',
    syncSkipped: '{{count}} property(ies) skipped (disabled or above plan limit).',
    syncFailedFor: '{{site}}: {{error}}',
    propertyReadOnlyError: 'This property is above your plan limit: it is read-only.',
    propertyDisabledError: 'This property is disabled: enable it to sync.',
    rename: 'Rename',
    renamed: 'Label saved.',
    disable: 'Disable',
    enable: 'Enable',
    disabledBadge: 'Disabled',
    disabledToast: 'Property disabled.',
    enabledToast: 'Property enabled.',
    remove: 'Remove',
    removeTitle: 'Stop tracking this property?',
    removeMessage: 'Stop tracking {{site}}. Keep its stored data (restored if you add it again) or purge it permanently.',
    removeKeep: 'Remove, keep data',
    removePurge: 'Remove and purge data',
    removed: 'Property removed (data kept).',
    removedPurged: 'Property removed, {{count}} row(s) purged.',
    lastError: 'Last error: {{error}}',
    propertySelect: 'Property',
    allProperties: 'All properties',
    breakdownTitle: 'By property',
    property: 'Property',
    noPropertyTab: 'No Search Console property tracked yet.',
    noPropertyTabHint: 'Add a property in Settings → Integrations → Google Search Console.',
    manageProperties: 'Manage properties',
    purgeMessageProperty: 'All Search Console rows stored for {{site}} will be permanently deleted.',
  },
  revenueSeo: {
    title: 'Revenue & SEO',
    subtitle: 'Connect your revenue (Stripe) and organic visibility (Search Console) to journey analytics.',
  },
  webTraffic: {
    tabTraffic: 'Traffic',
    tabGsc: 'Search Console',
    tabsLabel: 'Web traffic views',
  },
  experiments: {
    title: 'Experiments',
    subtitle:
      'Test your levers (pricing, landing, channel, onboarding) with SDK-assigned variants, measure on an event or a Stripe metric and decide on statistically sound results.',
    newExperiment: 'New experiment',
    empty: 'No experiments',
    emptyHint: 'Create an experiment, or start from a failed pricing-audit criterion.',
    selectHint: 'Select an experiment to see its results.',
    status_draft: 'Draft',
    status_running: 'Running',
    status_stopped: 'Stopped',
    status_decided: 'Decided',
    lever_pricing: 'Pricing',
    lever_landing: 'Landing page',
    lever_channel: 'Channel',
    lever_onboarding: 'Onboarding',
    variantsCount: '{{count}} variant(s)',
    wizardTitle: 'New experiment — step {{step}}/3',
    stepBasics: 'Hypothesis',
    stepVariants: 'Variants',
    stepMetric: 'Measurement',
    name: 'Name',
    namePlaceholder: 'Visible annual discount',
    key: 'Key (used by the SDK)',
    keyHint: 'Lowercase letters, digits and dashes. Unique for your account.',
    keyInvalid: 'Invalid key: lowercase letters, digits and dashes only.',
    hypothesis: 'Hypothesis',
    hypothesisPlaceholder: 'If we show the annual discount, more visitors will upgrade to a paid plan.',
    lever: 'Lever',
    auditLink: 'Link to a pricing-audit criterion',
    auditNone: 'None',
    auditPrefilled: 'Suggested hypothesis pre-filled from the criterion.',
    variantKey: 'Key',
    variantName: 'Name',
    variantDescription: 'Description (optional)',
    control: 'Control',
    addVariant: 'Add a variant',
    removeVariant: 'Remove',
    variantsHint: '2 to 5 variants. The first one is the control (current experience).',
    variantsInvalid: 'Each variant needs a unique key (lowercase, digits, dashes) and a name.',
    metricSource: 'Primary metric',
    metricEvent: 'SDK event',
    metricStripe: 'Stripe metric',
    eventName: 'Event name',
    eventNamePlaceholder: 'signup',
    stripeMetric: 'Stripe metric',
    stripe_signup: 'Signup',
    stripe_trial_start: 'Trial start',
    stripe_paid: 'Converted to paid',
    stripe_mrr: 'MRR',
    minSample: 'Minimum sample per variant',
    minSampleHint: 'At least 5. No winner is declared below this threshold.',
    startAt: 'Start (optional)',
    endAt: 'End (optional)',
    create: 'Create experiment',
    creating: 'Creating…',
    created: 'Experiment created.',
    start: 'Start',
    stop: 'Stop',
    started: 'Experiment started.',
    stopped: 'Experiment stopped.',
    decide: 'Decide',
    delete: 'Delete',
    deleteTitle: 'Delete the experiment?',
    deleteMessage: 'The experiment “{{name}}” and its results will be permanently deleted.',
    deleted: 'Experiment deleted.',
    metricLabel: 'Metric: {{metric}}',
    sampleLabel: 'Threshold: {{n}} exposures per variant',
    periodLabel: 'Period: {{from}} → {{to}}',
    auditRefLabel: 'Audit criterion: {{label}}',
    sdkUsage: 'On your site: karexia(\'variant\',\'{{key}}\',[{{variants}}])',
    resultsTitle: 'Live results',
    autoRefresh: 'Auto-refresh every 10 s',
    noData: 'No exposures recorded yet.',
    noDataHint: 'Start the experiment and call karexia(\'variant\', …) on your site.',
    insufficient: 'Insufficient sample ({{n}}/{{threshold}} per variant) — no winner can be declared.',
    ready: 'Sample sufficient — results are actionable.',
    winner: 'Leading variant: {{variant}}',
    noWinner: 'No variant stands out significantly.',
    method: 'Method: {{method}}',
    totalExposures: '{{count}} exposure(s) in total',
    variant: 'Variant',
    exposures: 'Exposures',
    conversions: 'Conversions',
    rate: 'Rate',
    ci: '95% CI',
    probBest: 'Prob. best',
    uplift: 'Uplift vs control',
    pValue: 'p-value',
    ratesChart: 'Conversion rate per variant',
    dailyChart: 'Daily conversion rate',
    decisionTitle: 'Decision',
    decidedOn: 'Decided on {{when}}',
    outcome_ship_variant: 'Ship variant {{variant}}',
    outcome_keep_control: 'Keep control',
    outcome_inconclusive: 'Inconclusive',
    decideTitle: 'Decide on the experiment',
    outcome: 'Decision',
    outcomeShip: 'Ship a variant',
    outcomeKeep: 'Keep control',
    outcomeInconclusive: 'Inconclusive',
    shipVariant: 'Variant to ship',
    note: 'Note',
    notePlaceholder: 'Context, learnings, next step…',
    decideAuditLink: 'Audit link (criterion)',
    decideWarnInsufficient: 'Warning: the sample is insufficient. A “ship” decision would not be statistically grounded.',
    confirmDecision: 'Save decision',
    decidedToast: 'Decision saved.',
  },
  audit: {
    title: 'Pricing page audit',
    subtitle:
      'Score your pricing page on 6 criteria (tier clarity, anchoring, annual discount, trial terms, FAQ, guarantee) and turn every failure into an experiment.',
    modeUrl: 'URL',
    modeHtml: 'HTML',
    modeText: 'Text',
    urlPlaceholder: 'https://example.com/pricing',
    htmlPlaceholder: '<html>… paste the page source …</html>',
    textPlaceholder: 'Paste the visible text of your pricing page…',
    evaluate: 'Evaluate',
    evaluating: 'Evaluating…',
    score: 'Score: {{score}}/{{max}}',
    passed: 'Passed',
    failed: 'Missing',
    evidence: 'Evidence: {{evidence}}',
    noEvidence: 'No evidence found on the page.',
    createExperiment: 'Create an experiment',
  },
};

export default dataLoopEn;
